import React, { useState } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Eye, 
  Trash2, 
  Download,
  Calendar,
  DollarSign,
  Percent
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import type { FinancialScenario, FinancialPlan } from '../../types/businessPlan';

interface ScenarioComparisonProps {
  basePlan: FinancialPlan;
  scenarios: FinancialScenario[];
  onDeleteScenario: (scenarioId: string) => Promise<void>;
  onViewScenario: (scenario: FinancialScenario) => void;
  className?: string;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export const ScenarioComparison: React.FC<ScenarioComparisonProps> = ({
  basePlan,
  scenarios,
  onDeleteScenario,
  onViewScenario,
  className = ''
}) => {
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);
  const [comparisonMetric, setComparisonMetric] = useState<'ca_total' | 'marge_nette' | 'point_mort_mois' | 'cashflow_cumule'>('ca_total');

  const toggleScenarioSelection = (scenarioId: string) => {
    setSelectedScenarios(prev => 
      prev.includes(scenarioId)
        ? prev.filter(id => id !== scenarioId)
        : [...prev, scenarioId].slice(0, 4) // Max 4 scenarios for comparison
    );
  };

  const getComparisonData = () => {
    const baseValue = basePlan.analysis_result.kpis[comparisonMetric];
    const data = [
      {
        name: 'Plan de Base',
        value: baseValue,
        color: '#1f2937'
      }
    ];

    selectedScenarios.forEach((scenarioId, index) => {
      const scenario = scenarios.find(s => s.id === scenarioId);
      if (scenario) {
        data.push({
          name: scenario.name,
          value: scenario.results[comparisonMetric],
          color: COLORS[index + 1]
        });
      }
    });

    return data;
  };

  const formatValue = (value: number, metric: string) => {
    switch (metric) {
      case 'ca_total':
      case 'cashflow_cumule':
        return new Intl.NumberFormat('fr-FR', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0
        }).format(value);
      case 'marge_nette':
        return `${value.toFixed(1)}%`;
      case 'point_mort_mois':
        return `${value} mois`;
      default:
        return value.toString();
    }
  };

  const getMetricLabel = (metric: string) => {
    switch (metric) {
      case 'ca_total': return 'Chiffre d\'Affaires Total';
      case 'marge_nette': return 'Marge Nette (%)';
      case 'point_mort_mois': return 'Point Mort (mois)';
      case 'cashflow_cumule': return 'Cashflow Cumulé';
      default: return metric;
    }
  };

  const comparisonData = getComparisonData();

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <BarChart3 className="w-6 h-6 text-indigo-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Historique et Comparaison</h2>
            <p className="text-sm text-gray-600">
              Comparez vos différents scénarios et versions
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <select
            value={comparisonMetric}
            onChange={(e) => setComparisonMetric(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="ca_total">Chiffre d'Affaires</option>
            <option value="marge_nette">Marge Nette</option>
            <option value="point_mort_mois">Point Mort</option>
            <option value="cashflow_cumule">Cashflow Cumulé</option>
          </select>
        </div>
      </div>

      {/* Scenarios List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Scénarios Sauvegardés ({scenarios.length})
        </h3>
        
        {scenarios.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Aucun scénario sauvegardé</p>
            <p className="text-sm mt-1">Créez des simulations pour les comparer ici</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Base Plan */}
            <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="rounded border-gray-300"
                />
                <div>
                  <p className="font-medium text-gray-900">📊 Plan de Base</p>
                  <p className="text-sm text-gray-600">
                    Version originale • {format(new Date(basePlan.created_at), 'dd MMM yyyy', { locale: fr })}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-900">
                  {formatValue(basePlan.analysis_result.kpis[comparisonMetric], comparisonMetric)}
                </p>
                <p className="text-xs text-gray-500">{getMetricLabel(comparisonMetric)}</p>
              </div>
            </div>

            {/* Scenarios */}
            {scenarios.map((scenario, index) => {
              const isSelected = selectedScenarios.includes(scenario.id);
              const baseValue = basePlan.analysis_result.kpis[comparisonMetric];
              const scenarioValue = scenario.results[comparisonMetric];
              const variation = ((scenarioValue - baseValue) / baseValue) * 100;
              
              return (
                <div
                  key={scenario.id}
                  className={`flex items-center justify-between p-4 border rounded-lg transition-all duration-200 hover:shadow-md ${
                    isSelected 
                      ? 'border-blue-300 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleScenarioSelection(scenario.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <p className="font-medium text-gray-900">{scenario.name}</p>
                      <p className="text-sm text-gray-600">{scenario.description}</p>
                      <p className="text-xs text-gray-500">
                        {format(new Date(scenario.created_at), 'dd MMM yyyy à HH:mm', { locale: fr })}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        {formatValue(scenarioValue, comparisonMetric)}
                      </p>
                      <div className={`flex items-center space-x-1 text-xs ${
                        variation > 0 ? 'text-green-600' : variation < 0 ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {variation > 0 ? (
                          <TrendingUp className="w-3 h-3" />
                        ) : variation < 0 ? (
                          <TrendingDown className="w-3 h-3" />
                        ) : null}
                        <span>{variation > 0 ? '+' : ''}{variation.toFixed(1)}%</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => onViewScenario(scenario)}
                        className="p-2 text-gray-400 hover:text-blue-600 rounded-lg transition-colors"
                        title="Voir les détails"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDeleteScenario(scenario.id)}
                        className="p-2 text-gray-400 hover:text-red-600 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Comparison Chart */}
      {selectedScenarios.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Comparaison : {getMetricLabel(comparisonMetric)}
          </h3>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis 
                  tickFormatter={(value) => 
                    comparisonMetric.includes('ca') || comparisonMetric.includes('cashflow')
                      ? `${(value / 1000).toFixed(0)}K€`
                      : comparisonMetric === 'marge_nette'
                        ? `${value}%`
                        : `${value}`
                  }
                />
                <Tooltip 
                  formatter={(value: number) => [formatValue(value, comparisonMetric), getMetricLabel(comparisonMetric)]}
                />
                <Bar 
                  dataKey="value" 
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};